// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from "react-intl";
import { Typography, makeStyles, Container, Button, Box } from '@material-ui/core';
import PropTypes from 'prop-types';

import { Loading } from '../../components';
import { getI18n } from "../../utils/intl";
import { getAuthSchemesData } from '../../reducers/login';
import { fetchPublicAuthSchemes } from "../../actions/login";
import { LoginProvider } from "./context";
import { LABELS, SchemeShape } from './constants';
import { goToPreviousPage } from "./utils";
import { useGlobalVariable } from './hooks';
import LoginForm from "./LoginForm";
import Version from "./Version";
import SignupButton from './SignupButton';
import BrandLogo from "./BrandLogo";
import AuthSchemeListItem from "./AuthSchemeListItem";
import AuthHubAuthScheme from './AuthHubAuthScheme';
import OktaAuthScheme from './OktaAuthScheme';
import GlobalErrors from './GlobalErrors';
import styles from './styles';
import { xssSanitize } from '../../utils';

const AUTH_SCHEME_METHOD_SAML_PROXIED = 'SAML_PROXIED';

const AUTH_SCHEME_TYPE_PROXY_SAML = 'proxy_saml';
const AUTH_SCHEME_TYPE_INTERNAL = 'internal';
const AUTH_SCHEME_TYPE_EXTERNAL = 'external';

const Login = () => {
  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const globalDispatch = useDispatch();

  const { data: authSchemesData, isLoading: isLoadingAuthSchemes, error: authSchemesError }
    = useSelector(getAuthSchemesData);

  const [selectedAuthScheme, setSelectedAuthScheme] = React.useState(null);
  const [shouldShowOnlyOkta, setShouldShowOnlyOkta] = React.useState(false);
  const [hasDetectedMultipleSessions, setHasDetectedMultipleSessions] = React.useState(false);
  const [shouldShowCredentialsError, setShouldShowCredentialsError] = React.useState(false);

  const [provider, setProvider] = useGlobalVariable('provider', 'provider', false);
  const [referrer] = useGlobalVariable('referrer', 'referrer', false, true);
  const [isEULArequired] = useGlobalVariable('eulaRequired', 'eulaRequired');
  const [toOktaConfig] = useGlobalVariable('toOktaConfig', 'to-okta-config');
  const [toAHubConfig] = useGlobalVariable('toAHubConfig', 'to-ahub-config');
  const [toDefaultConfig] = useGlobalVariable('toDefaultConfig', 'to-default-config');
  const [showOnlyOktaParam] = useGlobalVariable('fromApiHub', 'fromApiHub', false);

  const [eulaNotAccepted, setEulaNotAccepted] = useGlobalVariable('eulaNotAccepted', 'eula-not-accepted');
  const [loginError, setLoginError] = useGlobalVariable('loginError', 'login-error');
  const [sessionTimeout, setSessionTimeout] = useGlobalVariable('sessionTimeout', 'session-timeout');
  const [loginLocked, setLoginLocked] = useGlobalVariable('loginLocked', 'login-locked');
  const [contactAdmin, setContactAdmin] = useGlobalVariable('contactAdmin', 'contact-admin');
  const [unknownError, setUnknownError] = useGlobalVariable('unknownError', 'unknown-error');

  const globalErrors = {
    eulaNotAccepted, loginError, sessionTimeout, loginLocked, contactAdmin, unknownError,
  };

  const clearGlobalErrors = React.useCallback((errorToClear='') => {
    if (errorToClear) {
      const clearError = {
        eulaNotAccepted: setEulaNotAccepted,
        loginError: setLoginError,
        sessionTimeout: setSessionTimeout,
        loginLocked: setLoginLocked,
        contactAdmin: setContactAdmin,
        unknownError: setUnknownError,
      }
      clearError[errorToClear]?.(false);
      return;
    }
    setEulaNotAccepted(false);
    setLoginError(false);
    setSessionTimeout(false);
    setLoginLocked(false);
    setContactAdmin(false);
    setUnknownError(false);
  }, []);

  React.useEffect(() => {
    globalDispatch(fetchPublicAuthSchemes());
  }, []);
  
  React.useEffect(() => {
    if (authSchemesData?.authSchemes) {
      // if toDefaultConfig is true, and default config has to be authenticated on external page,
      // redirect to external page.
      if (toDefaultConfig && provider === null) {
        authSchemesData.authSchemes.forEach(authScheme => {
          // if default authscheme's authentication
          // is not handled by portal(of type portaldb login, LDAP)
          if (authScheme.uuid && authScheme.defaultConfig && !authScheme.credsReqd) {
            window.location.href = authScheme.url;
          }
        })
      }
    }
  }, [authSchemesData, toDefaultConfig, provider]);

  React.useEffect(() => {
    if (authSchemesData?.authSchemes && !selectedAuthScheme) {
      let defaultAuthScheme;
      let internalAuthScheme;
      let queryParamAuthScheme;
      let proxiedAuthScheme;
      
      authSchemesData.authSchemes.forEach(authScheme => {
        // To show login errors with corresponding authscheme as selectedAuthScheme
        if (provider !== null && authScheme.uuid === provider) {
          queryParamAuthScheme = authScheme;
        }
        // Store default authentication scheme in defaultAuthScheme
        // only if it is authenticated by portal
        if (authScheme.defaultConfig && authScheme.credsReqd) {
          defaultAuthScheme = authScheme;
        }
        if (authScheme.showFyp) {
          internalAuthScheme = authScheme;
        }
        if (authScheme.authMethod === AUTH_SCHEME_METHOD_SAML_PROXIED) {
          proxiedAuthScheme = authScheme;
        }
      });

      if (authSchemesData?.authSchemes?.length <= 1) {
        if (authSchemesData?.isProxiedAuthScheme &&
          authSchemesData.authSchemes?.[0]?. authMethod === AUTH_SCHEME_METHOD_SAML_PROXIED) {
          setSelectedAuthScheme(proxiedAuthScheme);
        } else {
          setSelectedAuthScheme(internalAuthScheme);
        }
        return;
      }

      if (proxiedAuthScheme && showOnlyOktaParam) {
        setSelectedAuthScheme(proxiedAuthScheme);
        setShouldShowOnlyOkta(true);
        return;
      }

      if (provider !== null) {
        setSelectedAuthScheme(queryParamAuthScheme);
        return;
      }

      if (toDefaultConfig && defaultAuthScheme) {
        setSelectedAuthScheme(defaultAuthScheme);
        return;
      }

      if ((toOktaConfig || toAHubConfig) && proxiedAuthScheme) {
        setSelectedAuthScheme(proxiedAuthScheme);
        return;
      }

      setSelectedAuthScheme(internalAuthScheme);
    }
  }, [authSchemesData, toDefaultConfig, showOnlyOktaParam, provider, selectedAuthScheme]);

  const selectedAuthSchemeType = React.useMemo(() => {
    if (!authSchemesData?.authSchemes) {
      return null;
    }
    if (!selectedAuthScheme) {
      return null;
    }

    if (authSchemesData.authSchemes.length <= 1) {
      if (authSchemesData.isProxiedAuthScheme &&
        authSchemesData.authSchemes?.[0]?. authMethod === AUTH_SCHEME_METHOD_SAML_PROXIED) {
        return AUTH_SCHEME_TYPE_PROXY_SAML;
      }
      return AUTH_SCHEME_TYPE_INTERNAL;
    }
    
    if (shouldShowOnlyOkta) {
      return AUTH_SCHEME_TYPE_PROXY_SAML;
    }

    if (selectedAuthScheme.showFyp) {
      return AUTH_SCHEME_TYPE_INTERNAL;
    }

    if (selectedAuthScheme.authMethod === AUTH_SCHEME_METHOD_SAML_PROXIED) {
      return AUTH_SCHEME_TYPE_PROXY_SAML;
    }

    if (toDefaultConfig && selectedAuthScheme.defaultConfig && selectedAuthScheme.credsReqd) {
      return AUTH_SCHEME_TYPE_EXTERNAL;
    }
    if (provider !== null && selectedAuthScheme.uuid === provider) {
      return AUTH_SCHEME_TYPE_EXTERNAL;
    }
   
    return AUTH_SCHEME_TYPE_INTERNAL;
  }, [selectedAuthScheme, authSchemesData, toDefaultConfig, provider, shouldShowOnlyOkta]);

  const ProxiedAuthScheme = (props) => props.scheme.isSamlProxied ?
    <AuthHubAuthScheme {...props} /> : <OktaAuthScheme {...props} />
  
  ProxiedAuthScheme.propTypes = {
    classes: PropTypes.object,
    scheme: PropTypes.object,
  };

  let content = null;
  if (selectedAuthScheme) {
    if (selectedAuthSchemeType === AUTH_SCHEME_TYPE_INTERNAL) {
      content = <AuthScheme id='internal-authscheme-form' scheme={selectedAuthScheme} classes={classes} />
    } else if (selectedAuthSchemeType === AUTH_SCHEME_TYPE_PROXY_SAML) {
      content = <ProxiedAuthScheme classes={classes} scheme={selectedAuthScheme} />
    } else if (selectedAuthSchemeType === AUTH_SCHEME_TYPE_EXTERNAL) {
      content = <AuthScheme id='external-authscheme-form' scheme={selectedAuthScheme} classes={classes} />
    }
  } else {
    content = <BrandLogo classes={classes} />
  }

  const shouldShowInAuthSchemesList = (scheme) => {
    const isOktaAuthScheme = scheme.authMethod === AUTH_SCHEME_METHOD_SAML_PROXIED;
    if (!selectedAuthScheme) {
      if (!isOktaAuthScheme) {
        return true;
      }
      if (scheme.isProxiedAuthScheme && scheme.samlPresent) {
        return true;
      }
      return false;
    }

    // Selected auth scheme is present now
    if (selectedAuthScheme.uuid === scheme.uuid) {
      // if this auth scheme is already selected, no need to show in the list
      return false;
    }

    // this auth scheme is not selected
    if (!isOktaAuthScheme) {
      // it is not okta auth schme, show in the list of auth schemes
      return true;
    }

    // this is okta auth scheme
    if (scheme.isProxiedAuthScheme && scheme.samlPresent) {
      // but it is okta proxied with saml present, so show in the list of auth schemes
      return true;
    }

    return false;
  }

  let mainContent = null;
  if (shouldShowOnlyOkta) {
    mainContent = <Button
      data-apim-test="login-cancel"
      variant="outlined" className={classes.focusStyles}
      color='secondary' onClick={goToPreviousPage}
    >
      <Typography className={classes.buttonContent} color='inherit'>
        {intl.getI18nMessage(LABELS.cancelButton)}
      </Typography>
    </Button>
  } else if (!hasDetectedMultipleSessions) {
    const schemesList = authSchemesData?.authSchemes?.filter(authScheme => {
      return shouldShowInAuthSchemesList(authScheme)
    })?.map(authScheme => {
      return <AuthSchemeListItem key={authScheme.uuid} scheme={authScheme} classes={classes} />
    });
    mainContent = (
      <>
        {schemesList?.length > 0
          ? <Box className={classes.authSchemesList}>
              {selectedAuthScheme && <Typography variant='h4' align="center" className={classes.or}>
                  - or -
                </Typography>
              }
              {schemesList}
            </Box>
          : null
        }
        {selectedAuthScheme && <SignupButton classes={classes} />}
      </>
    )
  }

  const loginContext = React.useMemo(() => ({
    selectedAuthScheme,
    hasDetectedMultipleSessions,
    isEULArequired,
    provider,
    referrer: xssSanitize(referrer),
    globalErrors,
    shouldShowCredentialsError,
    selectedAuthSchemeType,
    setHasDetectedMultipleSessions,
    setSelectedAuthScheme,
    setProvider,
    clearGlobalErrors,
    setShouldShowCredentialsError,
  }), [
    selectedAuthScheme,
    hasDetectedMultipleSessions,
    isEULArequired,
    provider,
    referrer,
    globalErrors,
    shouldShowCredentialsError,
    selectedAuthSchemeType,
    setHasDetectedMultipleSessions,
    setSelectedAuthScheme,
    setProvider,
    clearGlobalErrors,
    setShouldShowCredentialsError,
  ]);

  const shouldShowLoadingOverlay = isLoadingAuthSchemes || authSchemesError;
  return (
    <LoginProvider value={loginContext}>
      <Container className={classes.outerContainer}>
        {shouldShowLoadingOverlay && <Loading pageLoader fullPage />}
        <GlobalErrors />
        <Box id={'login-page-content'} className={classes.contentContainer}>
          {content}
          {mainContent}
          {!hasDetectedMultipleSessions && <Version />}
        </Box>
      </Container>
    </LoginProvider>
  )
};

const AuthScheme = ({ id, scheme, classes }) => {
  return <>
    <BrandLogo classes={classes}/>
    <LoginForm id={id} scheme={scheme} />
  </>
};

AuthScheme.propTypes= {
  id: PropTypes.string,
  scheme: PropTypes.shape(SchemeShape),
  classes: PropTypes.shape(),
}

export default Login;
